import React from 'react'
import { Button as DaisyButton, ButtonProps as DaisyButtonProps } from 'react-daisyui'
import { useHeyTheme } from '@/contexts/heyTheme.tsx'

interface ExtendedButtonProps extends DaisyButtonProps {
  /** When true, renders as pill-shaped (rounded-full) unless the theme uses rect buttons. */
  fullRounded?: boolean
}

/**
 * Theme-aware Button wrapper around DaisyUI's Button.
 *
 * - `buttonShape` (rounded/rect): Applied via CSS variable `--rounded-btn` in heyTheme.
 *   Works automatically, no per-button logic needed.
 * - `buttonVariant` (outline): Applied here as DaisyUI `variant` prop.
 *   A locally passed `variant` prop takes precedence.
 * - `fullRounded`: Adds `rounded-full` unless theme has rect buttons.
 *
 * Drop-in replacement: just swap the import from 'react-daisyui' to this file.
 */
const Button = React.forwardRef<HTMLButtonElement, ExtendedButtonProps>(({ fullRounded, className, ...props }, ref) => {
  const { buttonVariant, buttonShape } = useHeyTheme()

  const variant = props.variant ?? buttonVariant ?? undefined
  const roundedClass = fullRounded && buttonShape !== 'rect' ? 'rounded-full' : ''
  const combinedClassName = [roundedClass, className].filter(Boolean).join(' ') || undefined

  // @ts-ignore – DaisyUI ref typing
  return <DaisyButton {...props} variant={variant} className={combinedClassName} ref={ref} />
})

Button.displayName = 'Button'

export { Button }
export type { ExtendedButtonProps as ButtonProps }
